import { Formats } from 'next-intl';
import type { StorefrontPrice } from '@/api/menu-list/storefront-menu-snapshot.types';
import { PaymentType } from '@/shared/lib/priceRound';
import { Currency } from '@/shared/model/Currency';

type MenuItemEntry =
    | {
          item?: { id?: string } | null;
          body?: { id?: string } | null;
      }
    | null
    | undefined;

export type HashFromItemIdMap = Map<string, string>;

const EMPTY_HASH_FROM_ITEM_ID: HashFromItemIdMap = new Map();

/** One itemId→hash map per items-record identity (shared across callers). */
const hashFromItemIdByItems = new WeakMap<object, HashFromItemIdMap>();

/** Cached hash-from-itemId map for an items record (WeakMap by object identity). */
export const getHashFromItemIdMap = (
    menuItems: Record<string, MenuItemEntry> | null | undefined,
): HashFromItemIdMap => {
    if (!menuItems) return EMPTY_HASH_FROM_ITEM_ID;

    const cached = hashFromItemIdByItems.get(menuItems);
    if (cached) return cached;

    const map: HashFromItemIdMap = new Map();
    for (const [hash, entry] of Object.entries(menuItems)) {
        const id = entry?.body?.id ?? entry?.item?.id;
        if (id) map.set(id, hash);
    }
    hashFromItemIdByItems.set(menuItems, map);
    return map;
};

/**
 * Resolve addon member → menu hash.
 * Pass {@link getHashFromItemIdMap} result for O(1); a plain Record falls back to O(N) scan.
 */
export const getMemberHash = (
    member: { itemId?: string | null; hash?: string } | null | undefined,
    menuItemsOrMap: Record<string, MenuItemEntry> | HashFromItemIdMap | null | undefined,
): string | undefined => {
    if (!member) return undefined;
    const memberItemId = member.itemId;
    if (memberItemId && menuItemsOrMap) {
        if (menuItemsOrMap instanceof Map) {
            return menuItemsOrMap.get(memberItemId) ?? member.hash;
        }
        // ponytail: O(N) — prefer getHashFromItemIdMap when called in a loop
        return (
            Object.entries(menuItemsOrMap).find(([, item]) => {
                const id = item?.body?.id ?? item?.item?.id;
                return id === memberItemId;
            })?.[0] ?? member.hash
        );
    }
    return member.hash;
};

/**
 * The SINGLE_PICK member selected by default: the first one with a contained amount.
 * It is part of the item (free); only a switch away from it reaches the order.
 */
export const getContainedSinglePickHash = (
    members: Array<{ itemId?: string | null; hash?: string; amount?: { value?: string } | null }>,
    menuItemsOrMap: Record<string, MenuItemEntry> | HashFromItemIdMap | null | undefined,
): string | undefined => {
    const contained = members.find((member) => Number(member.amount?.value ?? 0) > 0);
    return contained ? getMemberHash(contained, menuItemsOrMap) : undefined;
};

export const normalizeAddonQty = (qty: number | boolean | string | null | undefined): number => {
    if (typeof qty === 'boolean') return qty ? 1 : 0;
    if (typeof qty === 'number') return qty;
    return 0;
};

export const isItemPriceConfigured = (
    item:
        | { prices?: Array<{ priceGroup?: { priceValues?: { length: number } } | null } | null> | null }
        | null
        | undefined,
): boolean => !!item?.prices?.some((price) => (price?.priceGroup?.priceValues?.length ?? 0) > 0);

/** Raw override price format from admin backend (before conversion to StorefrontPrice). */
type RawOverridePrice = {
    countryCode: string | null;
    prices: Array<{
        countryCode: string | null;
        currency: string;
        amount: string;
    }>;
};

type OverrideItem = {
    prices?: StorefrontPrice[] | RawOverridePrice[] | string | null;
} | null;

const isStorefrontPrices = (
    prices: StorefrontPrice[] | RawOverridePrice[] | string | null | undefined,
): prices is StorefrontPrice[] => {
    if (!Array.isArray(prices) || prices.length === 0) return false;
    const first = prices[0];
    return typeof first === 'object' && first !== null && 'priceGroup' in first;
};

const isRawOverridePrices = (
    prices: StorefrontPrice[] | RawOverridePrice[] | string | null | undefined,
): prices is RawOverridePrice[] => {
    if (!Array.isArray(prices) || prices.length === 0) return false;
    const first = prices[0];
    return typeof first === 'object' && first !== null && 'countryCode' in first && 'prices' in first;
};

const convertRawOverridePrices = (raw: RawOverridePrice[]): StorefrontPrice[] =>
    raw.map((overridePrice) => ({
        country: overridePrice.countryCode,
        priceGroup: {
            priceValues: overridePrice.prices.map((pv) => ({
                amount: pv.amount,
                currency: pv.currency,
                countryCode: pv.countryCode,
            })),
        },
    }));

type EffectivePricesInput = {
    overrideItem?: OverrideItem | string | null;
    item?: { prices?: StorefrontPrice[] | null } | null;
    body?: { prices?: StorefrontPrice[] | null } | null;
};

export function getEffectivePrices(menuListItem: EffectivePricesInput): StorefrontPrice[] {
    const basePrices = menuListItem.item?.prices ?? menuListItem.body?.prices ?? [];
    const overrideItem = menuListItem.overrideItem;

    if (!overrideItem || typeof overrideItem !== 'object') {
        return basePrices;
    }

    const overridePrices = overrideItem.prices;
    if (!overridePrices) {
        return basePrices;
    }

    if (isStorefrontPrices(overridePrices)) {
        return overridePrices;
    }

    if (isRawOverridePrices(overridePrices)) {
        return convertRawOverridePrices(overridePrices);
    }

    return basePrices;
}

type PriceTranslator = (key: 'WORDS.PRICE', values: { price: number }, formats: Formats) => string;

type Props = {
    amount: number | string;
    currency: Currency;
    paymentType?: PaymentType;
    maximumFractionDigits?: number;
    minimumFractionDigits?: number;
    t: PriceTranslator;
};

const getMaxFractionDigits = (currency: Currency, paymentType: PaymentType): number => {
    if (paymentType === 'card') {
        return 2;
    }
    if (currency === Currency.CZK) {
        return 0;
    }
    return 2;
};

export const getPriceValueTranslation = ({
    amount,
    currency,
    paymentType,
    maximumFractionDigits,
    minimumFractionDigits,
    t,
}: Props) => {
    return t(
        'WORDS.PRICE',
        {
            price: Number(amount),
        },
        {
            number: {
                currency: {
                    style: 'currency',
                    currency: currency,
                    maximumFractionDigits:
                        maximumFractionDigits ??
                        (paymentType !== undefined ? getMaxFractionDigits(currency, paymentType) : 2),
                    minimumFractionDigits: minimumFractionDigits ?? 0,
                },
            },
        },
    );
};
